// services/api.js
// Keeping your file structure, adding a safe global auth-expired flow + optional UX notice.

// ---------- API base URL ----------

// CRA dev proxy works ONLY with relative URLs.
// If you build absolute URLs to http://localhost:3000, you bypass the proxy.
function inferBaseFromWindow() {
  if (typeof window === "undefined") return "http://localhost:8000";

  const host = window.location.hostname;
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local");

  // In CRA dev, we want relative paths so "proxy" in package.json works.
  if (isLocal) return "";

  // In production (same-origin deployment), keep same-origin.
  return window.location.origin;
}

function normalizeBase(base) {
  if (!base) return "";
  return String(base).replace(/\/+$/, "");
}

const API_BASE = normalizeBase(
  (typeof process !== "undefined" && process.env?.REACT_APP_API_BASE) ||
    inferBaseFromWindow()
);

// ---------- Auth token helpers ----------

function getStoredToken() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("llm_audit_token");
  } catch {
    return null;
  }
}

function setStoredToken(token) {
  if (typeof window === "undefined") return;
  try {
    if (token) {
      window.localStorage.setItem("llm_audit_token", token);
    } else {
      window.localStorage.removeItem("llm_audit_token");
    }
  } catch {
    // ignore
  }
}

// ---------- Auth-expired event + notice helpers ----------

const AUTH_EXPIRED_EVENT = "mad:auth_expired";
const AUTH_EXPIRED_NOTICE_KEY = "mad_auth_expired_notice";

// throttle so multiple failing requests don't spam UI
let __lastAuthExpiredEmit = 0;

function setAuthExpiredNotice(message) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      AUTH_EXPIRED_NOTICE_KEY,
      String(message || "")
    );
  } catch {
    // ignore
  }
}

export function consumeAuthExpiredNotice() {
  if (typeof window === "undefined") return null;
  try {
    const msg = window.sessionStorage.getItem(AUTH_EXPIRED_NOTICE_KEY);
    if (msg) window.sessionStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
    return msg || null;
  } catch {
    return null;
  }
}

function emitAuthExpiredOnce(messageForUser) {
  if (typeof window === "undefined") return;

  const now = Date.now();
  if (now - __lastAuthExpiredEmit < 1500) return; // 1.5s throttle
  __lastAuthExpiredEmit = now;

  if (messageForUser) setAuthExpiredNotice(messageForUser);

  window.dispatchEvent(
    new CustomEvent(AUTH_EXPIRED_EVENT, {
      detail: { message: messageForUser || null },
    })
  );
}

export function onAuthExpired(handler) {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(AUTH_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
}

// error 401 for token rejection
async function authFetch(url, options = {}) {
  const token = getStoredToken();
  const headers = new Headers(options.headers || {});
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const res = await fetch(url, { ...options, headers });

  // ✅ important: don't hard reload; instead emit global auth-expired
  if (token && res.status === 401) {
    setStoredToken(null);

    emitAuthExpiredOnce(
      "Your session timed out due to inactivity. Please sign in again to continue."
    );
  }

  return res;
}

// ---------- Generic response handler ----------

function looksLikeHtml(text) {
  if (!text) return false;
  const t = String(text).trim().toLowerCase();
  return (
    t.startsWith("<!doctype html") ||
    t.startsWith("<html") ||
    t.includes("<head>") ||
    t.includes("<body>")
  );
}

function friendlyErrorMessage(res, rawText) {
  if (res.status === 401) {
    // Try to extract the actual error detail from the response (e.g., "Incorrect email or password")
    try {
      const parsed = JSON.parse(rawText);
      if (parsed.detail) return parsed.detail;
    } catch {}
    return "Your session expired. Please sign in again.";
  }
  if (res.status === 403) return "You don’t have permission to do that.";
  if (res.status === 404) return "That API endpoint doesn’t exist on the server.";
  if (res.status === 405) return "That action isn’t allowed here (wrong method).";

  if (looksLikeHtml(rawText)) {
    return "Something went wrong on the server. Please try again. If it keeps happening, contact support.";
  }

  return null;
}

async function handleResponse(res) {
  const text = await res.text();

  if (!res.ok) {
    let msg = "Unexpected error.";

    const friendly = friendlyErrorMessage(res, text);
    if (friendly) {
      throw new Error(friendly);
    }

    try {
      const json = text ? JSON.parse(text) : null;

      if (json) {
        if (json.error && typeof json.error === "string") {
          msg = json.error;
        } else if (json.error && typeof json.error.message === "string") {
          msg = json.error.message;
        } else if (typeof json.detail === "string") {
          msg = json.detail;
        } else if (json.detail && typeof json.detail.message === "string") {
          msg = json.detail.message;
        } else if (typeof json.message === "string") {
          msg = json.message;
        } else {
          msg = text || res.statusText;
        }
      } else {
        msg = text || res.statusText;
      }
    } catch {
      msg = text || res.statusText;
    }

    if (looksLikeHtml(msg)) {
      msg =
        "Something went wrong on the server. Please try again. If it keeps happening, contact support.";
    }

    throw new Error(msg);
  }

  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------------------
// Feature flags (frontend-enforced; stored in Settings)
// ---------------------------------------------------------------------------

let __cachedFeatureFlags = {
  copilotDisabled: false,
};

export function setCachedFeatureFlags(flags) {
  __cachedFeatureFlags = {
    copilotDisabled: !!flags?.copilotDisabled,
  };
}

export function getCachedFeatureFlags() {
  return { ...__cachedFeatureFlags };
}

export async function fetchGlobalFeatureFlags() {
  const data = await settingsList("global", null);
  const items = data?.items ?? data ?? [];

  const map = {};
  for (const it of items) {
    if (!it?.key) continue;
    map[it.key] = it.value;
  }

  const copilotDisabled =
    String(map["feature.copilot_disabled"] ?? "false") === "true";

  return { copilotDisabled };
}

function assertCopilotEnabled() {
  if (__cachedFeatureFlags?.copilotDisabled) {
    throw new Error("Copilot is disabled via Global Settings.");
  }
}

// ---------- Auth API ----------

export async function login(email, password) {
  const body = new URLSearchParams();
  body.append("username", email);
  body.append("password", password);

  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  const data = await handleResponse(res);
  if (data?.requires_2fa) {
    // Don't store token yet — user needs to complete 2FA
    return data;
  }
  if (data?.access_token) {
    setStoredToken(data.access_token);
  }
  return data;
}

export async function login2fa(pendingToken, code) {
  const res = await fetch(`${API_BASE}/auth/login/2fa`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pending_token: pendingToken, code }),
  });

  const data = await handleResponse(res);
  if (data?.access_token) {
    setStoredToken(data.access_token);
  }
  return data;
}

export async function tfa2faStatus() {
  const res = await authFetch(`${API_BASE}/auth/2fa/status`);
  return handleResponse(res);
}

export async function tfa2faSetupBegin() {
  const res = await authFetch(`${API_BASE}/auth/2fa/setup/begin`, { method: "POST" });
  return handleResponse(res);
}

export async function tfa2faSetupConfirm(code) {
  const res = await authFetch(`${API_BASE}/auth/2fa/setup/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  return handleResponse(res);
}

export async function tfa2faDisable(code) {
  const res = await authFetch(`${API_BASE}/auth/2fa/disable`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  return handleResponse(res);
}

export async function tfa2faAdminReset(userId) {
  const res = await authFetch(`${API_BASE}/auth/2fa/admin-reset/${userId}`, { method: "POST" });
  return handleResponse(res);
}

export async function getCurrentUser() {
  const res = await authFetch(`${API_BASE}/auth/me`);
  return handleResponse(res);
}

export async function refreshSession() {
  const res = await authFetch(`${API_BASE}/auth/refresh`, { method: "POST" });
  const data = await handleResponse(res);
  if (data?.access_token) {
    setStoredToken(data.access_token);
  }
  // Return user info so the caller can update state
  return getCurrentUser();
}

export function logout() {
  setStoredToken(null);
}

export async function changePassword(
  currentPassword,
  newPassword,
  newPasswordConfirm
) {
  const res = await authFetch(`${API_BASE}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    }),
  });
  return handleResponse(res);
}

// ---------- Brands (LLM module) ----------

export async function fetchBrands() {
  const res = await authFetch(`${API_BASE}/brands/`);
  return handleResponse(res);
}

// Brand aliases (LLM Discovery)
export async function fetchBrandAliases(brandId) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/aliases`);
  return handleResponse(res);
}

export async function addBrandAlias(brandId, alias) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/aliases`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ alias }),
  });
  return handleResponse(res);
}

export async function setBrandAliases(brandId, aliases) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/aliases`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ aliases }),
  });
  return handleResponse(res);
}

export async function deleteBrandAlias(brandId, aliasId) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/aliases/${aliasId}`, { method: "DELETE" });
  return handleResponse(res);
}

/**
 * Create a brand for LLM module.
 * scope:
 *  - "module": enable only for this module
 *  - "global": enable for both LLM + Ads
 */
export async function createBrand(payload, scope = "module") {
  const qs = new URLSearchParams();
  qs.set("scope", scope || "module");

  const res = await authFetch(`${API_BASE}/brands/?${qs.toString()}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/**
 * IMPORTANT: this does NOT delete the global brand.
 * It removes it from the LLM module (membership off).
 */
export async function deleteBrand(brandId) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ---------- Brands Hub (global brands management) ----------
// NOTE: BrandsHubPage expects fetchBrandHub() to return an ARRAY.
// Backend returns { items: [...] }, so we unwrap here.

export async function fetchBrandHub() {
  const res = await authFetch(`${API_BASE}/brands-hub/brands`);
  const data = await handleResponse(res);
  return Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
}

export async function brandHubCreate(payload) {
  const res = await authFetch(`${API_BASE}/brands-hub/brands`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
  return handleResponse(res); // { id, name, modules: { llm, ads } }
}

export async function brandHubRename(brandId, name) {
  const res = await authFetch(`${API_BASE}/brands-hub/brands/${brandId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return handleResponse(res); // updated row
}

export async function brandHubSetMembership(brandId, module, isEnabled) {
  const res = await authFetch(
    `${API_BASE}/brands-hub/brands/${brandId}/modules/${encodeURIComponent(
      module
    )}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_enabled: !!isEnabled }),
    }
  );
  return handleResponse(res); // updated row
}

export async function brandHubDelete(brandId) {
  const res = await authFetch(`${API_BASE}/brands-hub/brands/${brandId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ---------- CSV Upload ----------

export async function uploadCSV(file, brandId) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("brand_id", brandId);
  const res = await authFetch(`${API_BASE}/admin/upload-csv`, {
    method: "POST",
    body: fd,
  });
  return handleResponse(res);
}

// ---------- Questions ----------

export async function fetchQuestionsForBrand(brandId) {
  const res = await authFetch(`${API_BASE}/questions/brand/${brandId}`);
  return handleResponse(res);
}

export async function updateQuestion(brandId, questionId, newText, kind) {
  const fd = new FormData();
  if (newText !== undefined && newText !== null) fd.append("new_text", newText);
  if (kind === "organic" || kind === "prompted") fd.append("kind", kind);
  const res = await authFetch(`${API_BASE}/questions/${brandId}/${questionId}`, {
    method: "PUT",
    body: fd,
  });
  return handleResponse(res);
}

export async function deleteQuestion(brandId, questionId) {
  const res = await authFetch(`${API_BASE}/questions/${brandId}/${questionId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

export async function addQuestion(brandId, text, kind) {
  const fd = new FormData();
  fd.append("text", text);
  if (kind === "organic" || kind === "prompted") fd.append("kind", kind);
  const res = await authFetch(`${API_BASE}/questions/${brandId}`, {
    method: "POST",
    body: fd,
  });
  return handleResponse(res);
}

// ---------- Audits (SRA) ----------

export async function createAudit(payload) {
  const res = await authFetch(`${API_BASE}/audits/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function cancelRun(runId) {
  const res = await authFetch(`${API_BASE}/audits/run/${runId}/cancel`, { method: "POST" });
  return handleResponse(res);
}

export async function duplicateAudit(auditId) {
  const res = await authFetch(`${API_BASE}/audits/${auditId}/duplicate`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function fetchAuditsForBrand(brandId) {
  const res = await authFetch(`${API_BASE}/audits/brand/${brandId}`);
  return handleResponse(res);
}

export async function fetchAuditMatrix(auditId) {
  const res = await authFetch(`${API_BASE}/audits/${auditId}/matrix`);
  return handleResponse(res);
}

export async function startRun(run_id) {
  const res = await authFetch(`${API_BASE}/audits/run/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ run_id }),
  });
  return handleResponse(res);
}

export async function retryRun(run_id) {
  const res = await authFetch(`${API_BASE}/audits/run/retry-run`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ run_id }),
  });
  return handleResponse(res);
}

export async function retryOne(run_id, audit_question_id) {
  const res = await authFetch(`${API_BASE}/audits/run/retry-one`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ run_id, audit_question_id }),
  });
  return handleResponse(res);
}

export async function listVersions(audit_id, aq_id) {
  const res = await authFetch(
    `${API_BASE}/audits/${audit_id}/questions/${aq_id}/versions`
  );
  return handleResponse(res);
}

export async function editQuestionVersion(audit_id, aq_id, text) {
  const res = await authFetch(
    `${API_BASE}/audits/${audit_id}/questions/${aq_id}/edit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    }
  );
  return handleResponse(res);
}

export async function selectVersion(audit_id, aq_id, version_id) {
  const res = await authFetch(
    `${API_BASE}/audits/${audit_id}/questions/${aq_id}/select-version/${version_id}`,
    { method: "POST" }
  );
  return handleResponse(res);
}

export async function updateNotes(audit_id, aq_id, notes) {
  const res = await authFetch(
    `${API_BASE}/audits/${audit_id}/questions/${aq_id}/notes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ notes }),
    }
  );
  return handleResponse(res);
}

export async function fetchRunsForAudit(auditId) {
  const res = await authFetch(`${API_BASE}/audits/${auditId}/runs`);
  return handleResponse(res);
}

export async function fetchRunStatus(run_id) {
  const res = await authFetch(`${API_BASE}/audits/run/${run_id}/status`);
  return handleResponse(res);
}

/**
 * Streaming run. Returns a ReadableStreamDefaultReader over SSE lines.
 *
 * Protocol:
 *   {"type": "start",    "run_id": N, "total": N}
 *   {"type": "progress", "run_id": N, "completed": N, "total": N, "status": "ok"|"error"}
 *   {"type": "done",     "run_id": N, "completed": N, "total": N}
 *   {"type": "error",    "run_id": N, "error": "..."}
 */
export async function startRunStream(run_id) {
  const token = typeof window !== "undefined"
    ? (() => { try { return window.localStorage.getItem("llm_audit_token"); } catch { return null; } })()
    : null;

  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/audits/run/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify({ run_id }),
  });

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j?.detail || detail;
    } catch {}
    throw new Error(detail);
  }

  return res.body.getReader();
}

// ---------- Integrations ----------

export async function fetchSearchApiUsage() {
  const res = await authFetch(`${API_BASE}/integrations/searchapi-usage`);
  return handleResponse(res);
}

// ---------- Copilot (frontend guard) ----------

export async function copilotStart(payload) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function copilotListSessions(brandId) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/brands/${brandId}/sessions`);
  return handleResponse(res);
}

export async function copilotGetSession(sessionId) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}`);
  return handleResponse(res);
}

export async function copilotExportMd(sessionId) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/export-md`);
  if (!res.ok) throw new Error("Export failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `analysis_${sessionId}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function copilotGetContextBlob(sessionId) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/context-blob`);
  return handleResponse(res);
}

export async function copilotSend(sessionId, payload) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/**
 * Streaming send. Returns a ReadableStreamDefaultReader over SSE lines.
 * Each line arrives as a string.
 *
 * Protocol:
 *   "data: <token>"   — text delta (newlines escaped as \\n)
 *   "data: [DONE]"    — stream complete
 *   "data: {"error": "..."}" — error from server
 */
export async function copilotSendStream(sessionId, payload) {
  assertCopilotEnabled();
  const token = typeof window !== "undefined"
    ? (() => { try { return window.localStorage.getItem("llm_audit_token"); } catch { return null; } })()
    : null;

  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/copilot/sessions/${sessionId}/send_stream`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j?.detail || detail;
    } catch {}
    throw new Error(detail);
  }

  return res.body.getReader();
}

export async function copilotRetry(sessionId, payload = {}) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/retry`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function copilotRename(sessionId, payload) {
  assertCopilotEnabled();
  const res = await authFetch(
    `${API_BASE}/copilot/sessions/${sessionId}/rename`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  return handleResponse(res);
}

export async function copilotDestroy(sessionId) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ✅ PTY022: Branch
export async function copilotBranch(sessionId, payload) {
  assertCopilotEnabled();
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/branch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ---------- Attachments (Copilot-only) ----------

export async function copilotExportPptx(sessionId) {
  const res = await authFetch(`${API_BASE}/copilot/sessions/${sessionId}/export-pptx`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Export failed");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `MAD_Audit_${sessionId}.pptx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function uploadAttachment(file, brandId, sessionId) {
  assertCopilotEnabled();

  const fd = new FormData();
  fd.append("file", file);
  fd.append("brand_id", String(brandId));
  fd.append("session_id", String(sessionId));

  const res = await authFetch(`${API_BASE}/attachments/upload`, {
    method: "POST",
    body: fd,
  });
  return handleResponse(res);
}

export async function downloadAttachmentBlob(attachmentId) {
  assertCopilotEnabled();

  const res = await authFetch(`${API_BASE}/attachments/${attachmentId}/download`);
  if (!res.ok) {
    await handleResponse(res); // throws
  }
  return await res.blob();
}

// ---------- Settings (PTY012) ----------

export async function settingsList(scope_type, scope_id = null) {
  const qs = new URLSearchParams();
  qs.set("scope_type", scope_type);
  if (scope_id != null) qs.set("scope_id", String(scope_id));

  const res = await authFetch(`${API_BASE}/settings?${qs.toString()}`);
  return handleResponse(res);
}

export async function settingsUpsert(payload) {
  const res = await authFetch(`${API_BASE}/settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function getReportLayout(brandId = null) {
  const qs = new URLSearchParams();
  if (brandId != null) qs.set("brand_id", String(brandId));
  const res = await authFetch(
    `${API_BASE}/settings/report-layout${qs.toString() ? `?${qs.toString()}` : ""}`,
  );
  return handleResponse(res);
}

export async function putReportLayout({ scope_type = "global", scope_id = null, layout }) {
  const res = await authFetch(`${API_BASE}/settings/report-layout`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope_type, scope_id, layout }),
  });
  return handleResponse(res);
}

export async function resolveAdsAutoPrompt(brandId = null) {
  const qs = new URLSearchParams();
  if (brandId != null) qs.set("brand_id", String(brandId));
  const res = await authFetch(`${API_BASE}/settings/resolve-ads-auto-prompt?${qs.toString()}`);
  return handleResponse(res);
}

export async function resolveCopilotInitialPrompt(brandId = null) {
  const qs = new URLSearchParams();
  if (brandId != null) qs.set("brand_id", String(brandId));

  const res = await authFetch(
    `${API_BASE}/settings/resolve-copilot-initial-prompt?${qs.toString()}`
  );
  return handleResponse(res);
}

// ---------- Ads (Discovery) ----------

export async function adsHealth() {
  const res = await authFetch(`${API_BASE}/ads/health`);
  return handleResponse(res);
}

export async function adsListBrands() {
  const res = await authFetch(`${API_BASE}/ads/brands`);
  return handleResponse(res); // { items: [...] }
}

export async function adsListCompetitors(primaryBrandId) {
  const res = await authFetch(`${API_BASE}/ads/brands/${primaryBrandId}/competitors`);
  return handleResponse(res);
}

export async function adsAddCompetitor(primaryBrandId, competitorBrandId) {
  const res = await authFetch(`${API_BASE}/ads/brands/${primaryBrandId}/competitors/${competitorBrandId}`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function adsRemoveCompetitor(primaryBrandId, competitorBrandId) {
  const res = await authFetch(`${API_BASE}/ads/brands/${primaryBrandId}/competitors/${competitorBrandId}`, {
    method: "DELETE",
  });
  if (res.status === 204) return true;
  return handleResponse(res);
}

export async function adsStartIngestion(brand_id, source) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/start`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brand_id, source }),
  });
  return handleResponse(res);
}

export async function adsExecuteIngestion(runId, { dateFrom, dateTo, includeCompetitors } = {}) {
  const params = new URLSearchParams();
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  if (includeCompetitors) params.set("include_competitors", "true");
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await authFetch(`${API_BASE}/ads/ingestion/${runId}/execute${qs}`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function adsGetRunStats(runId) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/${runId}/stats`);
  return handleResponse(res);
}

export async function adsListRuns(brandId) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/brand/${brandId}/runs`);
  return handleResponse(res);
}

export async function adsListLogs(runId) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/${runId}/logs`);
  return handleResponse(res);
}

export async function adsFinalizeRun(runId, status = "success", error_summary = null) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/${runId}/finalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, error_summary }),
  });
  return handleResponse(res);
}

export async function adsListEntities(brandId, source = null) {
  const qs = new URLSearchParams();
  if (source) qs.set("source", source);
  const res = await authFetch(
    `${API_BASE}/ads/entities/brand/${brandId}?${qs.toString()}`
  );
  return handleResponse(res);
}

export async function adsUpsertEntity(payload) {
  const res = await authFetch(`${API_BASE}/ads/entities`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

/**
 * ✅ NEW: hard delete entity (not just active/inactive)
 * Expected backend route:
 *   DELETE /ads/entities/{entityId}
 */
export async function adsDeleteEntity(entityId) {
  const res = await authFetch(`${API_BASE}/ads/entities/${encodeURIComponent(String(entityId))}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

export async function adsListBrandAds(brandId, source, limit = 100, offset = 0, filters = {}, includeCompetitors = false) {
  const qs = new URLSearchParams();
  qs.set("source", source);
  qs.set("limit", String(limit));
  qs.set("offset", String(offset));
  if (includeCompetitors) qs.set("include_competitors", "true");
  if (filters.dateFrom) qs.set("date_from", filters.dateFrom);
  if (filters.dateTo) qs.set("date_to", filters.dateTo);
  if (filters.endDateFrom) qs.set("end_date_from", filters.endDateFrom);
  if (filters.endDateTo) qs.set("end_date_to", filters.endDateTo);
  if (filters.status) qs.set("status_filter", filters.status);
  if (filters.creativeType) qs.set("creative_type_filter", filters.creativeType);
  if (filters.headline) qs.set("headline_filter", filters.headline);
  if (filters.advertiser) qs.set("advertiser_filter", filters.advertiser);
  if (filters.advertiserName) qs.set("advertiser_name_filter", filters.advertiserName);
  if (filters.cta) qs.set("cta_filter", filters.cta);
  if (filters.body) qs.set("body_filter", filters.body);

  const res = await authFetch(
    `${API_BASE}/ads/brands/${brandId}/ads?${qs.toString()}`
  );
  return handleResponse(res);
}

export async function adsExportBrandAdsCsv(brandId, source, filters = {}) {
  const qs = new URLSearchParams();
  qs.set("source", source);
  if (filters.dateFrom) qs.set("date_from", filters.dateFrom);
  if (filters.dateTo) qs.set("date_to", filters.dateTo);
  if (filters.endDateFrom) qs.set("end_date_from", filters.endDateFrom);
  if (filters.endDateTo) qs.set("end_date_to", filters.endDateTo);
  if (filters.status) qs.set("status_filter", filters.status);
  if (filters.creativeType) qs.set("creative_type_filter", filters.creativeType);
  if (filters.headline) qs.set("headline_filter", filters.headline);
  if (filters.advertiser) qs.set("advertiser_filter", filters.advertiser);
  // Mirror the list-view filter set — advertiser_name and body were
  // silently dropped from export previously, returning more rows in
  // the CSV than the matrix showed.
  if (filters.advertiserName) qs.set("advertiser_name_filter", filters.advertiserName);
  if (filters.cta) qs.set("cta_filter", filters.cta);
  if (filters.body) qs.set("body_filter", filters.body);

  const res = await authFetch(
    `${API_BASE}/ads/brands/${brandId}/ads/export.csv?${qs.toString()}`
  );
  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try { const d = await res.json(); msg = d?.detail || msg; } catch {}
    throw new Error(msg);
  }
  return res.blob();
}

export async function adsGetAd(adId) {
  const res = await authFetch(`${API_BASE}/ads/ads/${adId}`);
  return handleResponse(res);
}

export async function adsAnalyzeAd(adId, brandId, analysisType = "single") {
  const qs = new URLSearchParams();
  if (brandId) qs.set("brand_id", String(brandId));
  if (analysisType !== "single") qs.set("analysis_type", analysisType);
  const q = qs.toString() ? `?${qs.toString()}` : "";
  const res = await authFetch(`${API_BASE}/ads/ads/${adId}/analyze${q}`, { method: "POST" });
  return handleResponse(res);
}

export async function adsGetAdAnalyses(adId) {
  const res = await authFetch(`${API_BASE}/ads/ads/${adId}/analyses`);
  return handleResponse(res);
}

export async function adsDeleteAdsBatch(adIds) {
  const res = await authFetch(`${API_BASE}/ads/ads/delete-batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ad_ids: adIds }),
  });
  return handleResponse(res);
}

// ─── MAD Pilot Chat Sessions ──────────────────────────────────────────────────

export async function adsCreateChatSession(brandId, adIds, name) {
  const res = await authFetch(`${API_BASE}/ads/chat/sessions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brand_id: brandId, ad_ids: adIds, name: name || null }),
  });
  return handleResponse(res);
}

export async function adsGetChatSession(sessionId) {
  const res = await authFetch(`${API_BASE}/ads/chat/sessions/${sessionId}`);
  return handleResponse(res);
}

export async function adsListChatSessions(brandId) {
  const res = await authFetch(`${API_BASE}/ads/brands/${brandId}/chat/sessions`);
  return handleResponse(res);
}

export async function adsRenameChatSession(sessionId, name) {
  const res = await authFetch(`${API_BASE}/ads/chat/sessions/${sessionId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  return handleResponse(res);
}

export async function adsDeleteChatSession(sessionId) {
  const res = await authFetch(`${API_BASE}/ads/chat/sessions/${sessionId}`, { method: "DELETE" });
  return handleResponse(res);
}

/**
 * Stream a message in a MAD Pilot session.
 *
 * onChunk(text)            — text token
 * onProgress(message)      — extraction progress update
 * onDone()                 — stream finished
 * onError(message)         — error
 *
 * snapshotRefs: { [ad_id]: creative_id } — ads that have cached screenshots (backend reads from disk)
 */
export async function adsChatSessionStream(
  { sessionId, message, model, pinnedAdIds, snapshotRefs },
  onChunk, onProgress, onDone, onError, onCostUpdate,
) {
  let res;
  try {
    res = await authFetch(`${API_BASE}/ads/chat/sessions/${sessionId}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        model: model || null,
        pinned_ad_ids: pinnedAdIds?.length ? pinnedAdIds : null,
        snapshot_refs: snapshotRefs && Object.keys(snapshotRefs).length ? snapshotRefs : null,
      }),
    });
  } catch (e) {
    onError?.(e?.message || "Network error");
    return;
  }
  if (!res.ok) {
    onError?.(`HTTP ${res.status}`);
    return;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6);
      if (payload === "[DONE]") { onDone?.(); return; }
      try {
        const parsed = JSON.parse(payload);
        if (typeof parsed === "string") { onChunk?.(parsed); }
        else if (parsed?.type === "progress") { onProgress?.(parsed.message); }
        else if (parsed?.type === "cost_update") { onCostUpdate?.(parsed); }
        else if (parsed?.error) { onError?.(parsed.error); return; }
      } catch {
        onChunk?.(payload);
      }
    }
  }
  onDone?.();
}

export async function adsGetCreativeSnapshot(creativeId) {
  const res = await authFetch(`${API_BASE}/ads/creatives/${creativeId}/snapshot`);
  return handleResponse(res);
}

export async function adsPreloadSnapshots(runId) {
  const res = await authFetch(`${API_BASE}/ads/ingestion/${runId}/preload-snapshots`, { method: "POST" });
  return handleResponse(res);
}

// ✅ Provider validate
export async function adsValidateProvider(source, brand_id, advertiser_ref) {
  const src = String(source || "meta").trim().toLowerCase();
  const qs = new URLSearchParams();
  qs.set("brand_id", String(brand_id));
  qs.set("advertiser_ref", String(advertiser_ref));

  const res = await authFetch(
    `${API_BASE}/ads/providers/${encodeURIComponent(src)}/validate?${qs.toString()}`
  );
  return handleResponse(res);
}

/**
 * ✅ NEW: Auto-resolve / search advertisers for a brand + provider.
 * Expected backend route (recommended):
 *   GET /ads/providers/{source}/search?brand_id=..&q=..&limit=..
 *
 * Returns:
 *   { items: [{ advertiser_ref, label?, confidence?, extra? }, ...] }
 *
 * If backend not implemented, UI will show a friendly message.
 */
export async function adsSearchProviderEntities(source, brand_id, q, limit = 15) {
  const src = String(source || "meta").trim().toLowerCase();
  const qs = new URLSearchParams();
  qs.set("brand_id", String(brand_id));
  qs.set("q", String(q || ""));
  qs.set("limit", String(limit));

  const res = await authFetch(
    `${API_BASE}/ads/providers/${encodeURIComponent(src)}/search?${qs.toString()}`
  );
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Cost Control (PTY027)
// ---------------------------------------------------------------------------

export async function costGetSummary() {
  const res = await authFetch(`${API_BASE}/cost/summary`);
  return handleResponse(res);
}

export async function costGetModelPrices() {
  const res = await authFetch(`${API_BASE}/cost/model-prices`);
  return handleResponse(res);
}

export async function costSaveModelPrice(data) {
  // data: { model_name, provider, input_price_per_1k, output_price_per_1k, per_request_surcharge }
  // POST for new, PUT for existing (caller decides based on isNew flag)
  const { isNew, ...body } = data;
  const res = await authFetch(
    isNew
      ? `${API_BASE}/cost/model-prices`
      : `${API_BASE}/cost/model-prices/${encodeURIComponent(body.model_name)}`,
    {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  return handleResponse(res);
}

export async function costDeleteModelPrice(modelName) {
  const res = await authFetch(
    `${API_BASE}/cost/model-prices/${encodeURIComponent(modelName)}`,
    { method: "DELETE" }
  );
  if (res.status === 204) return;
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Admin Panel
// ---------------------------------------------------------------------------

export async function adminListUsers() {
  const res = await authFetch(`${API_BASE}/admin-panel/users`);
  return handleResponse(res);
}

export async function adminCreateUser(data) {
  const res = await authFetch(`${API_BASE}/admin-panel/users`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function adminUpdateUser(userId, data) {
  const res = await authFetch(`${API_BASE}/admin-panel/users/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function adminDeleteUser(userId) {
  const res = await authFetch(`${API_BASE}/admin-panel/users/${userId}`, {
    method: "DELETE",
  });
  if (res.status === 204) return;
  return handleResponse(res);
}

// Domain categories
export async function adminListDomainCategories(search = "", category = "", source = "") {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (category) params.set("category", category);
  if (source) params.set("source", source);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/admin-panel/domain-categories${qs ? "?" + qs : ""}`);
  return handleResponse(res);
}

export async function adminUpdateDomainCategory(id, category) {
  const res = await authFetch(`${API_BASE}/admin-panel/domain-categories/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ category }),
  });
  return handleResponse(res);
}

export async function adminCreateDomainCategory(domain, category) {
  const res = await authFetch(`${API_BASE}/admin-panel/domain-categories`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ domain, category }),
  });
  return handleResponse(res);
}

export async function adminDeleteDomainCategory(id) {
  const res = await authFetch(`${API_BASE}/admin-panel/domain-categories/${id}`, { method: "DELETE" });
  if (res.status === 204) return;
  return handleResponse(res);
}

export async function adminGetSystemInfo() {
  const res = await authFetch(`${API_BASE}/admin-panel/system-info`);
  return handleResponse(res);
}

// Durable-job queue (ARQ + Redis) health snapshot. Admin-only.
// Shape: { redis: "up"|"down", redis_error, worker_status: "live"|"stale"|"missing",
//          worker_seen_seconds_ago, worker_heartbeat, queued_jobs }
export async function getQueueHealth() {
  const res = await authFetch(`${API_BASE}/system/queue-health`);
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Release Calendar
// ---------------------------------------------------------------------------

export async function getCurrentVersion() {
  try {
    const res = await fetch(`${API_BASE}/releases/current-version`);
    if (!res.ok) return "v0.2.0";
    const data = await res.json();
    return data.version || "v0.2.0";
  } catch { return "v0.2.0"; }
}

export async function listReleases() {
  const res = await authFetch(`${API_BASE}/releases`);
  return handleResponse(res);
}

export async function createRelease(data) {
  const res = await authFetch(`${API_BASE}/releases`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function updateRelease(id, data) {
  const res = await authFetch(`${API_BASE}/releases/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function deleteRelease(id) {
  const res = await authFetch(`${API_BASE}/releases/${id}`, { method: "DELETE" });
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Copilot Metrics
// ---------------------------------------------------------------------------

export async function getCopilotEnforcedPrompt() {
  const res = await authFetch(`${API_BASE}/settings/copilot-enforced-prompt`);
  return handleResponse(res);
}

export async function getCopilotAnalystDefault() {
  const res = await authFetch(`${API_BASE}/settings/copilot-analyst-default`);
  return handleResponse(res);
}

export async function copilotMetricTypes() {
  const res = await authFetch(`${API_BASE}/copilot-metrics/types`);
  return handleResponse(res);
}

export async function copilotMetricList(brandId = null) {
  const qs = brandId ? `?brand_id=${brandId}` : "";
  const res = await authFetch(`${API_BASE}/copilot-metrics/configs${qs}`);
  return handleResponse(res);
}

export async function copilotMetricCreate(data) {
  const res = await authFetch(`${API_BASE}/copilot-metrics/configs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function copilotMetricUpdate(configId, data) {
  const res = await authFetch(`${API_BASE}/copilot-metrics/configs/${configId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return handleResponse(res);
}

export async function copilotMetricDelete(configId) {
  const res = await authFetch(`${API_BASE}/copilot-metrics/configs/${configId}`, {
    method: "DELETE",
  });
  if (res.status === 204) return;
  return handleResponse(res);
}

export async function copilotMetricPreview(configId, auditId) {
  const res = await authFetch(
    `${API_BASE}/copilot-metrics/configs/${configId}/preview?audit_id=${auditId}`,
    { method: "POST" }
  );
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Forgot password / Reset password
// ---------------------------------------------------------------------------

export async function forgotPassword(email) {
  const res = await fetch(`${API_BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return handleResponse(res);
}

export async function resetPassword(token, newPassword) {
  const res = await fetch(`${API_BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export async function inviteUser(email, isAdmin = false) {
  const res = await authFetch(`${API_BASE}/auth/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, is_admin: isAdmin }),
  });
  return handleResponse(res);
}

export async function validateInvite(token) {
  const res = await fetch(`${API_BASE}/auth/invite/${encodeURIComponent(token)}/validate`);
  return handleResponse(res);
}

export async function acceptInvite(token, fullName, password) {
  const res = await fetch(`${API_BASE}/auth/accept-invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, full_name: fullName, password }),
  });
  return handleResponse(res);
}

export async function listInvitations() {
  const res = await authFetch(`${API_BASE}/auth/invitations`);
  return handleResponse(res);
}

export async function revokeInvitation(inviteId) {
  const res = await authFetch(`${API_BASE}/auth/invitations/${inviteId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

export async function resendInvitation(inviteId) {
  const res = await authFetch(`${API_BASE}/auth/invitations/${inviteId}/resend`, {
    method: "POST",
  });
  return handleResponse(res);
}

// ---------- Citations ----------

export async function citationsBrands() {
  const res = await authFetch(`${API_BASE}/citations/brands`);
  return handleResponse(res);
}

export async function citationsBrandDetail(brandId, auditId) {
  let url = `${API_BASE}/citations/brands/${brandId}`;
  if (auditId) url += `?audit_id=${auditId}`;
  const res = await authFetch(url);
  return handleResponse(res);
}

export async function citationsBrandAudits(brandId) {
  const res = await authFetch(`${API_BASE}/citations/brands/${brandId}/audits`);
  return handleResponse(res);
}

export async function citationsDomainDetail(domain) {
  const res = await authFetch(`${API_BASE}/citations/domains/${encodeURIComponent(domain)}`);
  return handleResponse(res);
}

export async function citationsDomainUrls(domain, brandId, auditId) {
  const params = new URLSearchParams();
  if (brandId) params.set("brand_id", brandId);
  if (auditId) params.set("audit_id", auditId);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/citations/domains/${encodeURIComponent(domain)}/urls${qs ? "?" + qs : ""}`);
  return handleResponse(res);
}

export async function citationsAuditDetail(domain, url, brandId) {
  const params = new URLSearchParams();
  if (domain) params.set("domain", domain);
  if (url) params.set("url", url);
  if (brandId) params.set("brand_id", brandId);
  const qs = params.toString();
  const res = await authFetch(`${API_BASE}/citations/audit-detail${qs ? "?" + qs : ""}`);
  return handleResponse(res);
}

export async function citationsSearch(query, brandId) {
  const params = new URLSearchParams({ q: query });
  if (brandId) params.set("brand_id", brandId);
  const res = await authFetch(`${API_BASE}/citations/search?${params.toString()}`);
  return handleResponse(res);
}

export async function citationsUpdateCategory(domain, category) {
  const res = await authFetch(`${API_BASE}/citations/domains/${encodeURIComponent(domain)}/category`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ category }),
  });
  return handleResponse(res);
}

export async function citationsReclassify(domains) {
  const res = await authFetch(`${API_BASE}/citations/reclassify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ domains }),
  });
  return handleResponse(res);
}

export async function citationsAddOwnedDomain(brandId, domain) {
  const res = await authFetch(`${API_BASE}/citations/brands/${brandId}/owned-domains`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ domain }),
  });
  return handleResponse(res);
}

export async function citationsRemoveOwnedDomain(brandId, domain) {
  const res = await authFetch(`${API_BASE}/citations/brands/${brandId}/owned-domains/${encodeURIComponent(domain)}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ---------- Brand Onboarding ----------

export async function brandOnboard(payload) {
  const res = await authFetch(`${API_BASE}/brands/onboard`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function brandAutoDetectDomains(brandId) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/auto-detect-domains`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function brandGetFullDetails(brandId) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/full-details`);
  return handleResponse(res);
}

export async function brandUpdateFullDetails(brandId, payload) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/full-details`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

// ---------- Question Templates (Phase 2/3) ----------

export async function questionTemplatesList(industry = null) {
  const qs = industry ? `?industry=${encodeURIComponent(industry)}` : "";
  const res = await authFetch(`${API_BASE}/question-templates${qs}`);
  return handleResponse(res);
}

export async function questionTemplateGet(templateId) {
  const res = await authFetch(`${API_BASE}/question-templates/${templateId}`);
  return handleResponse(res);
}

export async function questionTemplateCreate(payload) {
  const res = await authFetch(`${API_BASE}/question-templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function questionTemplateUpdate(templateId, payload) {
  const res = await authFetch(`${API_BASE}/question-templates/${templateId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function questionTemplateSetItems(templateId, items) {
  const res = await authFetch(`${API_BASE}/question-templates/${templateId}/items`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  return handleResponse(res);
}

export async function questionTemplateDelete(templateId) {
  const res = await authFetch(`${API_BASE}/question-templates/${templateId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ---------- Model-Tier Templates ----------

export async function modelTierTemplatesList() {
  const res = await authFetch(`${API_BASE}/model-tier-templates`);
  return handleResponse(res);
}

export async function modelTierTemplateGet(templateId) {
  const res = await authFetch(`${API_BASE}/model-tier-templates/${templateId}`);
  return handleResponse(res);
}

export async function modelTierTemplateCreate(payload) {
  const res = await authFetch(`${API_BASE}/model-tier-templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function modelTierTemplateUpdate(templateId, payload) {
  const res = await authFetch(`${API_BASE}/model-tier-templates/${templateId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function modelTierTemplateDelete(templateId) {
  const res = await authFetch(`${API_BASE}/model-tier-templates/${templateId}`, {
    method: "DELETE",
  });
  return handleResponse(res);
}

// ---------- Analysis Runs ----------

export async function analysisRunsList({ brandId = null, status = null, includeDeleted = false, assignee = null, active = false } = {}) {
  const qs = new URLSearchParams();
  if (brandId != null) qs.set("brand_id", String(brandId));
  if (status) qs.set("status", status);
  if (includeDeleted) qs.set("include_deleted", "true");
  if (assignee) qs.set("assignee", String(assignee));
  if (active) qs.set("active", "true");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await authFetch(`${API_BASE}/analysis-runs${suffix}`);
  return handleResponse(res);
}

export async function analysisRunGet(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}`);
  return handleResponse(res);
}

export async function analysisRunPreviewTitle(brandId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/preview-title?brand_id=${brandId}`);
  return handleResponse(res);
}

export async function analysisRunCreate(payload) {
  const res = await authFetch(`${API_BASE}/analysis-runs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function analysisRunTransitionStatus(runId, status) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });
  return handleResponse(res);
}

// LLM171: atomic abort — transitions the run to `failed` AND cancels every
// in-flight SRA run under its audit so workers stop burning tokens within
// seconds. Returns { analysis_run, cancelled_sra_run_ids }.
export async function analysisRunAbort(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/abort`, {
    method: "POST",
  });
  return handleResponse(res);
}

// Pivot B: run-scoped draft question set. Pass null to clear (reverts the
// run to using the brand library as-is).
export async function analysisRunSetDraftQuestions(runId, draftQuestions) {
  const res = await authFetch(
    `${API_BASE}/analysis-runs/${runId}/draft-questions`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ draft_questions: draftQuestions }),
    },
  );
  return handleResponse(res);
}

export async function analysisRunEffectiveQuestions(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/effective-questions`);
  return handleResponse(res);
}

// LLM178: batch "non-terminal runs per brand" for hub chips. Returns
// [{brand_id, count, runs: [{id, title, status, assignee_user_id, assignee_name}]}].
export async function analysisRunsActiveByBrand() {
  const res = await authFetch(`${API_BASE}/analysis-runs/active-by-brand`);
  return handleResponse(res);
}

// LLM183: per-stage cost breakdown for the hub card popover.
export async function analysisRunCostBreakdown(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/cost-breakdown`);
  return handleResponse(res);
}

// LLM179: list every PPTX artifact version for a run (newest first).
export async function analysisRunPptxArtifacts(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/pptx-artifacts`);
  return handleResponse(res);
}

// LLM179: download a specific PPTX version. Returns { blob, filename }.
export async function analysisRunPptxVersionDownload(runId, versionNo) {
  const res = await authFetch(
    `${API_BASE}/analysis-runs/${runId}/pptx-artifacts/${versionNo}/download`,
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const m = /filename="?([^"]+)"?/i.exec(cd);
  return { blob, filename: m ? m[1] : `pptx_v${versionNo}.pptx` };
}

// LLM184: kick a revision of an analysis. Bumps revision_count, captures
// the latest alias decision, clears the copilot_session_id, and transitions
// the run back to running_analysis. Allowed from analysis_complete or ready.
export async function analysisRunRevise(runId, { acceptedAliases = [], rejectedAliases = [] } = {}) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/revise`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      accepted_aliases: acceptedAliases,
      rejected_aliases: rejectedAliases,
    }),
  });
  return handleResponse(res);
}

// Branch an analysis run: create a sibling run from the same audit,
// preserving the original analysis. New run goes directly to
// running_analysis (skipping Stage 1 + 2). Returns { id, parent_run_id, status, run }.
export async function analysisRunBranch(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/branch`, {
    method: "POST",
  });
  return handleResponse(res);
}

// Pivot C: atomically overwrite a brand's library question set. 409 with
// detail.error === "library_overwrite_blocked" when other runs are in Stage 1
// on the same brand. Pass requesting_run_id so the caller's own run is
// excluded from the blocker check.
export async function brandQuestionsOverwrite(brandId, questions, requestingRunId = null) {
  const res = await authFetch(`${API_BASE}/brands/${brandId}/questions/overwrite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      questions: questions.map((q) => ({
        text: q.question_text || q.text || "",
        // Persist the per-question kind. Without this the backend used
        // to default every saved-to-library question to 'organic',
        // silently dropping the 'prompted' classification users set
        // in Stage 1.
        kind: q.kind === "prompted" ? "prompted" : "organic",
        language: q.language || "en",
      })),
      requesting_run_id: requestingRunId,
    }),
  });
  return handleResponse(res);
}

// LLM198: reassign a run. Pass null to clear. Only the current assignee or
// an admin may reassign.
export async function analysisRunAssign(runId, userId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/assign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId }),
  });
  return handleResponse(res);
}

// LLM198: active-user directory for pickers.
export async function fetchUserDirectory() {
  const res = await authFetch(`${API_BASE}/auth/directory`);
  return handleResponse(res);
}

export async function analysisRunSetAliasesPreflight(runId, payload) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/aliases-preflight`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return handleResponse(res);
}

export async function analysisRunAliasPreflight(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/alias-preflight`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function analysisRunLinkAudit(runId, auditId, matrixHash = null) {
  const body = { audit_id: auditId };
  if (matrixHash) body.audit_matrix_hash = matrixHash;
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/audit-linkage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

export async function analysisRunLinkSession(runId, copilotSessionId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/session-linkage`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ copilot_session_id: copilotSessionId }),
  });
  return handleResponse(res);
}

export async function analysisRunPptxDownloadUrl(runId) {
  // Returns a URL for the latest artifact. Caller must attach auth header via fetch.
  return `${API_BASE}/analysis-runs/${runId}/pptx-download/latest`;
}

export async function analysisRunPptxDownload(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/pptx-download/latest`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
}

export async function analysisRunSoftDelete(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}`, { method: "DELETE" });
  return handleResponse(res);
}

export async function analysisRunPptxLatest(runId) {
  const res = await authFetch(`${API_BASE}/analysis-runs/${runId}/pptx-artifacts/latest`);
  return handleResponse(res);
}

// ---------- Notifications (LLM197) ----------

export async function notificationsList({ unreadOnly = false, limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams();
  if (unreadOnly) params.set("unread_only", "true");
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const res = await authFetch(`${API_BASE}/notifications?${params.toString()}`);
  return handleResponse(res);
}

export async function notificationsUnreadCount() {
  const res = await authFetch(`${API_BASE}/notifications/unread-count`);
  return handleResponse(res);
}

export async function notificationMarkRead(notificationId) {
  const res = await authFetch(`${API_BASE}/notifications/${notificationId}/mark-read`, {
    method: "POST",
  });
  return handleResponse(res);
}

export async function notificationsMarkAllRead() {
  const res = await authFetch(`${API_BASE}/notifications/mark-all-read`, { method: "POST" });
  return handleResponse(res);
}
